import React, { useState, useEffect, useCallback, useRef } from 'react';
import Constants from '../../../Constants.json';

let logoutTimer;
let refreshTimer;

const AuthContext = React.createContext({
  token: '',
  refreshToken: '',
  role: '',
  isLoggedIn: false,
  login: (token) => { },
  logout: () => { },
});

const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();
  return adjExpirationTime - currentTime;
};

function accessTokenExpirationIso(accessToken) {
  try {
    const payload = JSON.parse(
      atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    if (payload.exp) return new Date(payload.exp * 1000).toISOString();
  } catch (_) {
    /* ignore */
  }
  return new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString();
}

async function requestTokenRefresh(refreshToken) {
  const base = (Constants.BACKEND_SERVER_BASE_URL || '').replace(/\/$/, '');
  if (!base || !refreshToken) return null;
  try {
    const res = await fetch(`${base}/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });
    if (!res.ok) return null;
    return res.json();
  } catch (_) {
    return null;
  }
}

const retrieveStoredToken = () => {
  const storedToken = localStorage.getItem('token');
  const storedrefreshToken = localStorage.getItem('refreshToken');
  const storedExpirationDate = localStorage.getItem('expirationTime');
  const storedRole = localStorage.getItem('role');

  if (!storedToken || !storedExpirationDate) {
    return null;
  }

  const remainingTime = calculateRemainingTime(storedExpirationDate);

  // Token déjà expiré : on garde le refresh pour une tentative silencieuse au montage
  if (remainingTime <= 0) {
    if (storedrefreshToken) {
      return {
        token: storedToken,
        refreshToken: storedrefreshToken,
        duration: 0,
        role: storedRole,
        needsRefresh: true,
      };
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expirationTime');
    localStorage.removeItem('role');
    return null;
  }

  return {
    token: storedToken,
    refreshToken: storedrefreshToken,
    duration: remainingTime,
    role: storedRole,
    needsRefresh: false,
  };
};

export const AuthContextProvider = (props) => {
  const tokenData = retrieveStoredToken();
  const schedulingRef = useRef(false);

  let initialToken;
  let initialRefreshToken;
  let initialRole;
  if (tokenData) {
    initialToken = tokenData.token;
    initialRefreshToken = tokenData.refreshToken;
    initialRole = tokenData.role;
  }

  const [token, setToken] = useState(initialToken);
  const [refreshToken, setRefreshToken] = useState(initialRefreshToken);
  const [role, setUserRole] = useState(initialRole);

  const userIsLoggedIn = !!token;

  const clearTimers = () => {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
      logoutTimer = null;
    }
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  };

  const logoutHandler = useCallback(() => {
    setToken(null);
    setRefreshToken(null);
    setUserRole(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expirationTime');
    localStorage.removeItem('role');
    clearTimers();
  }, []);

  const scheduleSession = useCallback((expirationTime, currentRefresh, currentRole) => {
    clearTimers();
    const remainingTime = calculateRemainingTime(expirationTime);
    if (remainingTime <= 0) {
      logoutHandler();
      return;
    }

    // Rafraîchir ~90s avant l'expiration de l'access token
    const refreshIn = Math.max(remainingTime - 90_000, Math.min(remainingTime / 2, remainingTime - 5_000));
    if (currentRefresh && refreshIn > 0 && refreshIn < remainingTime) {
      refreshTimer = setTimeout(async () => {
        if (schedulingRef.current) return;
        schedulingRef.current = true;
        const data = await requestTokenRefresh(currentRefresh);
        schedulingRef.current = false;
        if (!data?.access) {
          logoutHandler();
          return;
        }
        const nextRefresh = data.refresh || currentRefresh;
        const nextExp = accessTokenExpirationIso(data.access);
        localStorage.setItem('token', data.access);
        localStorage.setItem('refreshToken', nextRefresh);
        localStorage.setItem('expirationTime', nextExp);
        if (currentRole) localStorage.setItem('role', currentRole);
        setToken(data.access);
        setRefreshToken(nextRefresh);
        scheduleSession(nextExp, nextRefresh, currentRole);
      }, refreshIn);
    }

    logoutTimer = setTimeout(logoutHandler, remainingTime);
  }, [logoutHandler]);

  const loginHandler = (access, nextRefresh, nextRole, expirationTime) => {
    setToken(access);
    setRefreshToken(nextRefresh);
    setUserRole(nextRole);
    localStorage.setItem('role', nextRole);
    localStorage.setItem('token', access);
    localStorage.setItem('refreshToken', nextRefresh);
    localStorage.setItem('expirationTime', expirationTime);
    scheduleSession(expirationTime, nextRefresh, nextRole);
  };

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      if (!tokenData) return;

      if (tokenData.needsRefresh) {
        const data = await requestTokenRefresh(tokenData.refreshToken);
        if (cancelled) return;
        if (!data?.access) {
          logoutHandler();
          return;
        }
        const nextRefresh = data.refresh || tokenData.refreshToken;
        const nextExp = accessTokenExpirationIso(data.access);
        loginHandler(data.access, nextRefresh, tokenData.role, nextExp);
        return;
      }

      scheduleSession(
        localStorage.getItem('expirationTime'),
        tokenData.refreshToken,
        tokenData.role
      );
    };

    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- boot once from stored session
  }, []);

  const contextValue = {
    token: token,
    refreshToken: refreshToken,
    role: role,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
